import axios from "axios";
import { defineStore } from "pinia";
import { useAppState } from "./AppState";
import { useConversation } from "../Stores/Conversation";
import { nextTick } from "vue";
import router from "../router";

const FULL_MODE = "fullscreen";
const PANEL_MODE = "accordion";
const SPACES = "spaces";
const BOARDS = "boards";
const ITEMS = "items";

const INVITE = {
    ITEM: "item",
    ITEM_GROUP: "item-group",
    BOARD: "board",
    BOARD_GROUP: "board-group",
    SPACE_GROUP: "workspace-group",
    SPACE: "workspace",
};

const ITEM_TYPE = {
    TASK: "task",
};

import Utilities from "../Helpers/utilities";
import { useTeamPanel } from "./TeamPanel";
import { useTeam } from "../Components/Custom/Team/Store/Team";
import { useUserStore } from "./User";
import { useTimeTrackerStore } from "./TimeTracker";
import { useWorkspaceDnd } from "./WorkspaceDragAndDrop";
import { getChinUsers } from "../Helpers/chin";
import utilities from "../Helpers/utilities";
import { blobToBase64 } from "../Helpers/workspace";
import { useInvitation } from "./Invitation";
import { RouteHelper } from "../Helpers/RouteHelper";
import { useActivity } from "./Activity";
import { useWorkspaceTree } from "./WorkspaceTree";

export const WORKSPACE_TYPES = [
    {
        type: "workspace",
        title: "Common Workspace",
        subtitle:
            "Create a standard 3-section workspace consistent of Spaces, Boards, and Items in kanban style.",
        icon: "notes",
    },
    {
        type: "import_workspace",
        title: "Import Workspace",
        subtitle:
            "Import workspace data from Jira, Trello, Asana, Slack, Prospus or other sources.",
        icon: "mdi-file-import",
        staticIcon: true,
        staticIconType: "mdi",

        disabled: false,
    },
    {
        type: "workstation",
        title: "Workstation Connect",
        subtitle:
            "Download our free desktop application and collect desktop screenshots and productivity data.",
        icon: "computer",
        disabled: true,
    },
    {
        type: "external_connect",
        title: "External Connect",
        subtitle:
            "Create a simple link to any other application you use, with a 3-part kanban style workspace.",
        icon: "link",
        disabled: true,
    },
    {
        type: "feedback",
        title: "WordPress Connect",
        subtitle:
            "Add our sales chat, technical support chat, and collaboration features directly to your website.",
        disabled: true,
        icon: "",
    },
];

export const useWorkspace = defineStore("workspace", {
    state: () => {
        return {
            new_group_default_label: "Unnamed Group",
            space_categories: [],
            active_panel: "spaces",
            active_space: null,
            active_board: null,
            active_item: null,
            last_clicked_entity: { type: null, entity: null },
            active_view_mode: PANEL_MODE,
            active_chat_data: null,
            invited_data: null,
            hovered_space: null,
            hovered_board: null,
            hovered_item: null,
            selected_space_for_saving: null,
            create_new_workspace_toggle: false,
            editor_view: false,
            import_workspace_mode: false,
            import_step: null,
            import_has_user_input: false,
            import_workspace_persisted_state: null,

            are_space_boards_being_loaded: false,
            are_board_items_being_loaded: false,
            new_space: false,
            new_space_category: null,
            invite_type: null,
            checked_invite_profile_url: null,
            item_panel_state: null,
            document_error_message:
                "Failed to save Document updates, pls save editor content in your local editor",
            expanded_space: false,
            is_editing_item: false,
            is_hover_menu_disabled: false,
            last_updated_item: null,
            is_invitation_in_progress: false,
            chin: {
                item_profile_ids: [],
                board_profile_ids: [],
                space_profile_ids: [],
                item_group_profile_ids: [],
                board_group_profile_ids: [],
                space_group_profile_ids: [],
            },
            chin_selected: {
                item_profile_ids: [],
                board_profile_ids: [],
                space_profile_ids: [],
                item_group_profile_ids: [],
                board_group_profile_ids: [],
                space_group_profile_ids: [],
            },
            previous_active_item: null,
            new_space_options: false,
            is_scrolling_categories: false,
            fakeSpaceDetails: {
                show: false,
                categoryId: null,
            },
            activeItemCategory: null,
            activeBoardCategory: null,
            activeSpaceCategory: null,
            new_item_category_id: null,
            activeItemSavedScrollPosition: null,
            documentDetails: {
                open: false,
                conv_id: undefined,
                panel: "",
                is_open_from_hover_menu: false,
            },
            quickActionDrawerShow: false,
            quickActionCheckboxModel: false,
            WorkstationConnectDrawerShow: false,
            panels: {
                ITEMS,
                BOARDS,
                SPACES,
            },
            active_item_locator_temp: null,
            active_item_locator: null,
            active_item_locator_type: null,
            active_item_locator_cancel: false,

            live_sync_updated_data: {
                space_categories: false,
                space_categories_data: null,
                space: false,
                space_data: null,
                board_categories: false,
                board_categories_data: null,
                board: false,
                board_data: null,
                item_categories: false,
                item_categories_data: null,
            },

            active_item_not_found: false,

            runningCreateItemIds: {
                ids: new Map(),
                last: null,
            },
        };
    },
    getters: {
        is_panel_mode: (state) => state.active_view_mode === PANEL_MODE,
        is_full_mode: (state) => state.active_view_mode === FULL_MODE,
        is_invite_on_item: (state) => state.invite_type === INVITE.ITEM,
        is_invite_on_item_group: (state) =>
            state.invite_type === INVITE.ITEM_GROUP,
        is_invite_on_board: (state) => state.invite_type === INVITE.BOARD,
        is_invite_on_board_group: (state) =>
            state.invite_type === INVITE.BOARD_GROUP,
        is_invite_on_space_group: (state) =>
            state.invite_type === INVITE.SPACE_GROUP,
        is_invite_space: (state) => state.invite_type === INVITE.SPACE,
        is_active_panel_spaces: (state) => state.active_panel === SPACES,
        is_active_panel_boards: (state) => state.active_panel === BOARDS,
        is_active_panel_items: (state) => state.active_panel === ITEMS,
        get_active_panel: (state) => state.active_panel,
        get_selected_space_for_saving: (state) =>
            state.selected_space_for_saving,
        is_extended_space_active: (state) => state.expanded_space,
        space_categories_url: () => `/workspace-categories`,
        spaces_url: () => `/workspaces`,
        board_categories_url: (state) =>
            `/workspaces/${state.active_space.slug}/board-categories`,
        boards_url: (state) => `/workspaces/${state.active_space.slug}/boards`,
        item_categories_url: (state) =>
            `${state.boards_url}/${state.active_board.slug}/item-categories`,
        items_url: (state) =>
            `${state.boards_url}/${state.active_board?.slug}/items`,
        document_url: (state) =>
            `${state.boards_url}/${state.active_board?.slug}/items/${state.active_item?.slug}/document`,
        item_document_url: (state) =>
            `${state.boards_url}/${state.active_board?.slug}/items/${state.active_item?.slug}/document`,
        chin_users: (state) => {
            return getChinUsers({
                profiles: useUserStore().profiles,
                all_profile_ids: state.chin.item_profile_ids,
                selected_profile_ids: state.chin_selected.item_profile_ids,
            });
        },
        chin_item_group_users: (state) => {
            return getChinUsers({
                profiles: useUserStore().profiles,
                all_profile_ids: state.chin.item_group_profile_ids,
                selected_profile_ids:
                    state.chin_selected.item_group_profile_ids,
            });
        },
        chin_workspace_users: (state) => {
            return getChinUsers({
                profiles: useUserStore().profiles,
                all_profile_ids: state.chin.space_profile_ids,
                selected_profile_ids: state.chin_selected.space_profile_ids,
            });
        },
        chin_space_group_users: (state) => {
            return getChinUsers({
                profiles: useUserStore().profiles,
                all_profile_ids: state.chin.space_group_profile_ids,
                selected_profile_ids:
                    state.chin_selected.space_group_profile_ids,
            });
        },
        chin_board_users: (state) => {
            return getChinUsers({
                profiles: useUserStore().profiles,
                all_profile_ids: state.chin.board_profile_ids,
                selected_profile_ids: state.chin_selected.board_profile_ids,
            });
        },
        chin_board_group_users: (state) => {
            return getChinUsers({
                profiles: useUserStore().profiles,
                all_profile_ids: state.chin.board_group_profile_ids,
                selected_profile_ids:
                    state.chin_selected.board_group_profile_ids,
            });
        },
        has_active_item: (state) =>
            !Boolean(Object.keys(state.active_item ?? {}).length),
        fetching_item: () => useWorkspaceTree().fetching_item,
        isItemPanelDocumentOpened: (state) => {
            const { panel, open } = state.documentDetails;
            return open && panel === state.panels.ITEMS;
        },
        isBoardPanelDocumentOpened: (state) => {
            const { panel, open } = state.documentDetails;
            return open && panel === state.panels.BOARDS;
        },
        isSpacePanelDocumentOpened: (state) => {
            const { panel, open } = state.documentDetails;
            return open && panel === state.panels.SPACES;
        },
        isDocumentOpen: (state) => {
            const { open } = state.documentDetails;
            return open;
        },
        has_live_sync_updated_space_categories: (state) =>
            state.live_sync_updated_data.space_categories,
        has_live_sync_updated_space: (state) =>
            state.live_sync_updated_data.space,
        has_live_sync_updated_board_categories: (state) =>
            state.live_sync_updated_data.board_categories,
        has_live_sync_updated_board: (state) =>
            state.live_sync_updated_data.board,
        has_live_sync_updated_item_categories: (state) =>
            state.live_sync_updated_data.item_categories,

        get_live_sync_updated_space_categories_data: (state) =>
            state.live_sync_updated_data.space_categories_data,
        get_live_sync_updated_space_data: (state) =>
            state.live_sync_updated_data.space_data,
        get_live_sync_updated_board_categories_data: (state) =>
            state.live_sync_updated_data.board_categories_data,
        get_live_sync_updated_board_data: (state) =>
            state.live_sync_updated_data.board_data,
        get_live_sync_updated_item_categories_data: (state) =>
            state.live_sync_updated_data.item_categories_data,
    },
    actions: {
        updateLiveSyncUpdatedAt(type, data = null) {
            if (
                !type ||
                !Object.prototype.hasOwnProperty.call(
                    this.live_sync_updated_data,
                    type,
                )
            )
                return;
            this.live_sync_updated_data[type] = Boolean(data);
            this.live_sync_updated_data[type + "_data"] = data;
        },
        toggleQuickAccessDrawer() {
            this.quickActionDrawerShow = !this.quickActionDrawerShow;
        },
        updateQuickAccessDrawer(val = false) {
            this.quickActionDrawerShow = val;
        },
        updateQuickActionCheckboxModel(val = false) {
            this.quickActionCheckboxModel = val;
        },
        toggleWorkstationConnectDrawer() {
            this.WorkstationConnectDrawerShow =
                !this.WorkstationConnectDrawerShow;
        },
        setWorkstationConnectDrawer(val = false) {
            this.WorkstationConnectDrawerShow = val;
        },
        removeChinUsersBasedOnType(type, user_ids = []) {
            if (type == BOARDS) {
                this.chin.board_profile_ids =
                    this.chin.board_profile_ids.filter(
                        (userId) => !user_ids.includes(userId),
                    );
            } else if (type === ITEMS) {
                this.chin.item_profile_ids = this.chin.item_profile_ids.filter(
                    (userId) => !user_ids.includes(userId),
                );
            } else {
                this.chin.space_profile_ids =
                    this.chin.space_profile_ids.filter(
                        (userId) => !user_ids.includes(userId),
                    );
            }
        },
        toggleNewWorkspaceDrawer(show, categoryId = null) {
            this.fakeSpaceDetails = {
                show,
                categoryId,
            };
        },
        setImportWorkspaceMode(enabled = false) {
            this.import_workspace_mode = enabled;
            if (!enabled) {
                this.import_step = null;
                this.import_has_user_input = false;
            }
        },
        setImportStep(step) {
            this.import_step = step;
        },
        setImportHasUserInput(value) {
            this.import_has_user_input = value;
        },
        setImportWorkspacePersistedState(payload) {
            this.import_workspace_persisted_state = payload;
        },
        clearImportWorkspacePersistedState() {
            this.import_workspace_persisted_state = null;
        },
        setCurrentBreadcrumb(breadcrumbs) {
            this.breadcrumbs = breadcrumbs;
        },
        setPanelMode() {
            this.active_view_mode = PANEL_MODE;
        },
        setFullMode() {
            this.active_view_mode = FULL_MODE;
        },
        setSelectedSpaceForSaving(space) {
            this.selected_space_for_saving = space;
        },
        setInvitationInProgress(value) {
            this.is_invitation_in_progress = value;
        },
        toggleNewSpace(category = null) {
            this.expanded_space = false;
            this.new_space = !this.new_space;
            this.new_space_category = category;
        },
        setActivePanel(newValue) {
            this.active_panel = newValue;
            useAppState().updateActivePanel(newValue);
        },
        openSpacesPanel() {
            this.setActivePanel(SPACES);
        },
        openBoardsPanel() {
            this.setActivePanel(BOARDS);
        },
        openItemsPanel() {
            this.setActivePanel(ITEMS);
        },
        spaceBoardsLoadingStarted() {
            this.are_space_boards_being_loaded = 1;
        },
        spaceBoardsLoadingCompleted() {
            this.are_space_boards_being_loaded = 0;
        },
        boardItemsLoadingStarted() {
            this.are_board_items_being_loaded = 1;
        },
        boardItemsLoadingCompleted() {
            this.are_board_items_being_loaded = 0;
        },
        dataLoadingCompleted() {
            this.is_data_being_loaded = 0;
        },
        getOwnerIdForSpace(space_or_space_id) {
            if (!space_or_space_id) return null;

            if (typeof space_or_space_id == "object")
                space_or_space_id = space_or_space_id.id;
            const space = this.getSpaceById(space_or_space_id);
            return space?.owner_id;
        },
        getSpaceOwnerId(space = null) {
            return (space ?? this.active_space)?.owner_id;
        },
        isItemOwner(item) {
            return (
                item?.user_profile_id ==
                useUserStore().getCurrentUserProfileId()
            );
        },
        setActiveSpace(newValue) {
            // this.active_space = newValue
            this.active_chat_data = newValue;
        },
        setLastClickedEntity(type, entity) {
            // Validate type
            if (!["space", "board", "item"].includes(type)) {
                console.warn(`Invalid type for last_clicked_entity: ${type}`);
                return;
            }

            // Only store entity if it has sequence_number (required for routing)
            if (entity && entity.sequence_number) {
                this.last_clicked_entity = { type, entity };
            }
        },
        clearLastClickedEntity() {
            this.last_clicked_entity = { type: null, entity: null };
        },
        resetItemsPanel() {
            this.setActiveItem(null);
            this.setEditorView(false);
        },
        resetEditorIfOpenedOnPrevioulySelectedBoardItem(newlySelectedBoard) {
            if (
                this.active_board &&
                this.active_board.id == newlySelectedBoard.id
            )
                return;

            this.resetItemsPanel();
        },
        setActiveBoard(board, autoRedirect = true) {
            if (!board?.id) {
                this.active_board = {};
                return console.log("you can't open a Board not created yet");
            }

            if (board.id == this.active_board?.id) {
                // board is already selected.
                return;
            }

            this.boardItemsLoadingStarted();
            this.resetEditorIfOpenedOnPrevioulySelectedBoardItem(board);
            board.redirect = autoRedirect;
            this.active_board = board;
            this.active_chat_data = board;
            this.loadItems(board);

            const app_state_store = useAppState();
            app_state_store.updateBoardId(board.id);

            // Update last_clicked_entity for routing
            if (board && board.sequence_number) {
                this.setLastClickedEntity("board", board);
            }
        },
        setActiveChatData(data) {
            if (!data?.id) return (this.active_chat_data = {});

            if (!data.document) this.setItemDocument(data);

            this.active_chat_data = data;
        },
        setActiveChatDataEmpty() {
            this.active_chat_data = null;
        },
        setActiveItem(item, is_set_selected_item = true, autoRedirect = true) {
            if (!item?.id) return (this.active_item = {});

            if (
                !useWorkspaceDnd().selected_items.length &&
                is_set_selected_item
            )
                useWorkspaceDnd().clearSelectedItems(item);

            this.previous_active_item = item;
            item.redirect = autoRedirect;
            this.active_item = item;
            this.active_chat_data = item;
            this.setItemDocument(item);
            const app_state_store = useAppState();
            app_state_store.updateSelectedItemId(item.id);

            // Update last_clicked_entity for routing (only from Work panel)
            if (item && item.sequence_number && !app_state_store.is_team) {
                this.setLastClickedEntity("item", item);
            }
        },
        async hasSpaceCategoryAccess(space_category_id) {
            try {
                const { data } = await axios.get(
                    `/work-panel/workspace-groups/${space_category_id}/permissions/create`,
                );
                return !!data?.data;
            } catch (error) {
                console.log(error);
                return false;
            }
        },
        setMissingDocumentOnItemsForBoard(board) {
            board.item_categories.forEach((ic) => {
                ic.items?.forEach((item) => {
                    useWorkspace().setItemDocument(item);
                });
            });
        },
        setItemDocument(item) {
            if (item.document?.id) return;

            if (!item.document) item.document = {};

            item.document.documentable_type = "item";
            item.document.documentable_id = item.id;
            item.document.content = "";
            item.document.title = item.name;

            return item;
        },
        setActiveItemEmpty() {
            this.active_item = null;
        },
        setHoveredSpace(newValue) {
            this.hovered_space = newValue;
        },
        setHoveredBoard(newValue) {
            this.hovered_board = newValue;
        },
        setHoveredItem(newValue) {
            this.hovered_item = newValue;
        },
        setSpaces(spaces) {
            this.space_categories = spaces;
            this.hover_spaces = spaces;
        },
        setInviteType(type) {
            this.invite_type = type;
        },
        setCheckedInviteProfile_url(url) {
            this.checked_invite_profile_url = url;
        },
        areWorkspaceBoardsLoaded() {
            return (
                this.active_space.has_boards_loaded ||
                this.active_space?.board_categories?.length
            );
        },
        resetActiveSpace() {
            this.active_space = {};
            this.resetActiveBoard();
        },
        resetActiveBoard() {
            this.active_board = {};
            this.resetItemsPanel();
        },
        setActiveSpaceBoardItemOnBreadcrumb(space, board, item, type) {
            this.setActiveWorkspace(space);
            this.setActiveBoard(board);
            this.setActiveItem(item);

            if (type == "Space") {
                this.openBoardsPanel();
            } else if (type == "Board") {
                this.openBoardsPanel();
            }
        },
        setActiveWorkspace(space, autoRedirect = true) {
            if (!space?.id || space.id == this.active_space?.id)
                return console.log("fake Space or currently selected board");

            this.spaceBoardsLoadingStarted();
            space.redirect = autoRedirect;
            this.active_space = space;
            this.active_chat_data = space;
            this.resetActiveBoard();
            this.loadBoards(space);

            const app_state_store = useAppState();
            app_state_store.updateWorkspaceId(space.id);

            // Update last_clicked_entity for routing
            if (space && space.sequence_number) {
                this.setLastClickedEntity("space", space);
            }
        },
        async createSpaceCategory(name) {
            this.space_categories.splice(0, 0, { name });
            const { data } = await axios.post(this.space_categories_url, {
                name,
            });
            this.space_categories.splice(0, 1, data);
        },
        async loadBoards(space) {
            if (space?.has_boards_loaded || space?.board_categories?.length)
                return this.spaceBoardsLoadingCompleted();

            await this.fetchBoards(space);
        },

        async fetchBoards(space) {
            if (!space.slug) {
                console.log("space is empty, can't be used to fetch boards");
                return;
            }
            const { data } = await axios.get(
                `/workspaces/${space.slug}/boards`,
            );
            space.board_categories = data;
            space.has_boards_loaded = true;
            this.spaceBoardsLoadingCompleted();
        },
        areBoardItemsLoaded() {
            return (
                this.active_board.has_items_loaded ||
                this.active_board?.item_categories?.length
            );
        },
        async loadItems(board, space = null) {
            if (!board) return console.log("empty board to load items ..");

            if (board.has_items_loaded || board.item_categories?.length)
                return this.boardItemsLoadingCompleted();

            space = space ?? this.active_space;
            await this.fetchItems(board, space);
        },
        async fetchItems(board, space) {
            if (!space?.slug || !board?.slug) {
                console.log("fetchItems..", space, board);
                return;
            }
            const { data } = await axios.get(
                `/workspaces/${space.slug}/boards/${board.slug}/items`,
            );
            // const { data } = await axios.get(`${this.boards_url}/${board.slug}/items`)
            board.item_categories = data;
            board.has_items_loaded = true;
            this.new_item_category_id = board.item_categories[0]?.id;

            if (this.item_panel_state?.is_doc_view)
                this.setMissingDocumentOnItemsForBoard(board);

            this.boardItemsLoadingCompleted();
        },
        setEditorView(value) {
            this.editor_view = value;
        },

        openEditor(item_id = null, trackItemScrollPosition = false) {
            item_id = item_id ?? this.active_item?.id;

            if (!item_id) return;

            if (trackItemScrollPosition) {
                const elId = `auto-scroll-${this.active_item?.id}${this.active_item?.slug}`;
                const element = document?.getElementsByClassName(elId)?.[0];

                if (this.isElementInViewport(element)) {
                    const categoryElement = element.closest(
                        ".propus-items-container",
                    );
                    const categoriesElement = element.closest(
                        ".prospus-draggable-categories",
                    );

                    this.activeItemSavedScrollPosition = {
                        item: {
                            x: categoryElement.scrollLeft,
                            y: categoryElement.scrollTop,
                        },
                        category: {
                            x: categoriesElement.scrollLeft,
                            y: categoriesElement.scrollTop,
                        },
                    };
                } else {
                    this.activeItemSavedScrollPosition = null;
                }
            }

            this.setEditorView(true);
            useWorkspaceDnd().forceStopMultiSelect();

            const app_state_store = useAppState();
            app_state_store.updateEditor(item_id);
        },

        loadLastOpenedSpaceAndBoard(data) {
            const { workspace, board } = data;

            this.replaceSpace(workspace);
            this.replaceBoard(board);
        },
        restoreActivePanel(active_panel) {
            if (![SPACES, BOARDS, ITEMS].includes(active_panel)) return;

            this.setActivePanel(active_panel);
        },
        removeItemById(item_id) {
            item_id && useWorkspaceTree().removeItemById(item_id);
        },
        removeSpaceGroupById(space_group_id) {
            space_group_id &&
                useWorkspaceTree().removeWorkspaceCategoryById(space_group_id);
        },
        removeSpaceById(space_id) {
            space_id && useWorkspaceTree().removeWorkspaceById(space_id);
        },
        removeBoardGroupById(board_group_id) {
            board_group_id &&
                useWorkspaceTree().removeBoardCategoryById(board_group_id);
        },
        removeBoardById(board_id) {
            board_id && useWorkspaceTree().removeBoardById(board_id);
        },
        removeItemGroupById(item_group_id) {
            item_group_id &&
                useWorkspaceTree().removeItemCategoryById(item_group_id);
        },
        findItemInActiveBoard(item_id) {
            return this.findItemInBoard(item_id, this.active_board);
        },
        findItemInBoard(item_id, board) {
            if (!board || !item_id) return null;
            let item_found = null;
            board?.item_categories?.find((ic) =>
                ic.items?.find((item) =>
                    item.id == item_id ? (item_found = item) : false,
                ),
            );
            return item_found;
        },
        getSpaceById(space_id) {
            return useWorkspaceTree().getWorkspaceById(space_id);
        },
        getSpaceByItemId(item_id) {
            const tree = useWorkspaceTree();
            const item = tree.getItemById(item_id);

            return tree.getWorkspaceById(item?.workspaceId);
        },
        getSpaceBoardForItemGroup(item_group_id) {
            const tree = useWorkspaceTree();
            const item_category = tree.getItemCategoryById(item_group_id);
            const board = tree.getBoardById(item_category?.boardId);

            return {
                board: board,
                space: tree.getWorkspaceById(board?.workspaceId),
                item_category: item_category,
            };
        },
        getSpaceBoardForItem(item_id) {
            const tree = useWorkspaceTree();
            const item = tree.getItemById(item_id);
            const item_category = item?.itemCategoryId
                ? tree.getItemCategoryById(item.itemCategoryId)
                : null;
            const board = tree.getBoardById(
                item_category?.boardId ?? item?.boardId,
            );

            return {
                item: item,
                board: board,
                space: tree.getWorkspaceById(
                    board?.workspaceId ?? item?.workspaceId,
                ),
                item_category: item_category,
            };
        },

        async openItemWithWorkspaceAndBoardIfNotOpened(
            item_id = null,
            is_scroll_to = false,
            is_toggle_loader = true,
        ) {
            const app_state_store = useAppState();

            if (!item_id) return console.log("can't search for invalid item");

            if (is_toggle_loader) {
                app_state_store.togglePageLoader(true);
            }

            const { space, board, item } =
                await this.getOrLoadSpaceAndBoardForItemId(item_id);

            if (!item || !board || !space) {
                this.setIsFetchingItem(false);
                if (is_toggle_loader) {
                    app_state_store.togglePageLoader(false);
                }
                return;
            }

            this.setActiveWorkspace(space);
            this.setActiveBoard(board);
            this.setActiveItem(item);

            useWorkspaceDnd().clearSelectedItems(item);
            if (is_scroll_to) {
                timeOutHelper(() => {
                    window.scrollTo(
                        "category-group-id-" + item.item_category_id,
                    );
                }, 1);
            }

            this.openItemsPanel();

            this.setIsFetchingItem(false);

            if (is_toggle_loader) {
                app_state_store.togglePageLoader(false);
            }

            this.active_item_not_found = false;
        },

        // setSpaceBoardItem(
        //     space = null,
        //     board = null,
        //     item = null,
        //     is_scroll_to = false,
        //     is_toggle_loader = true,
        // ) {
        //     debugger
        //     console.log('this.space_categories1', this.space_categories, space)
        //     let temp_space, temp_board, temp_item;
        //     const app_state_store = useAppState();

        //     this.space_categories.forEach(space_cat => {
        //         if (space_cat.id === space.workspace_category_id) {
        //             let space_index = space_cat?.workspaces.findIndex(cat => cat.id === space.id);

        //             if (space_index === undefined || space_index === -1) {
        //                 let space_index = space_cat.workspaces.push(space);
        //                 space_index = space_index - 1;
        //             }

        //             temp_space = space_cat.workspaces[space_index];

        //             if (!temp_space?.board_categories) {
        //                 temp_space.board_categories = [];
        //             }

        //             let board_cat_index = temp_space?.board_categories?.findIndex(
        //                 board_cat => board_cat?.id === board?.board_category_id
        //             );

        //             if (board_cat_index === undefined || board_cat_index === -1) {
        //                 //space_cat.workspaces.push(space);
        //                 //temp_space.board_categories.push(board.board_category);
        //                 board_cat_index = temp_space?.board_categories.push(board.board_category);
        //                 board_cat_index = board_cat_index - 1;
        //             }

        //             const board_category = temp_space?.board_categories[board_cat_index];

        //             if (!board_category?.boards) {
        //                 board_category.boards = [];
        //             }

        //             let board_index = board_category?.boards?.findIndex(
        //                 board_item => board_item?.id === board?.id
        //             );

        //             if (board_index === undefined || board_index === -1) {
        //                 //space_cat.workspaces.push(space);
        //                 //temp_space.board_categories.push(board.board_category);
        //                 board_index = board_category?.boards.push(board);
        //                 board_index = board_index - 1;
        //             }

        //             temp_board = board_category?.boards[board_index];

        //             if (!temp_board?.item_categories) {
        //                 temp_board.item_categories = [];
        //             }

        //             let item_cat_index = temp_board?.item_categories?.findIndex(
        //                 item_cat => item_cat?.id === item?.item_category_id
        //             );

        //             if (item_cat_index === undefined || item_cat_index === -1) {
        //                 //space_cat.workspaces.push(space);
        //                 //temp_space.board_categories.push(board.board_category);
        //                 item_cat_index = temp_board?.item_categories.push(item.item_category);
        //                 item_cat_index = item_cat_index - 1;
        //             }

        //             const item_category = temp_board?.item_categories[item_cat_index];

        //             if (!item_category?.items) {
        //                 item_category.items = [];
        //             }

        //             let item_index = item_category?.items?.findIndex(
        //                 _item => _item?.id === item?.id
        //             );

        //             if (item_index === undefined || item_index === -1) {
        //                 //space_cat.workspaces.push(space);
        //                 //temp_space.board_categories.push(board.board_category);
        //                 item_index = item_category?.items.push(item);
        //                 item_index = item_index - 1;
        //             }

        //             temp_item = item_category?.items[item_index];
        //             // const board_category = temp_space?.board_categories[board_cat_index];
        //             //let board_index = board_category
        //         }
        //     })

        //     console.log('this.space_categories', this.space_categories, space)
        //     this.setActiveWorkspace(temp_space);
        //     this.setActiveBoard(temp_board);
        //     this.setActiveItem(temp_item);
        // },

        findItemInWorkspaces(item_id, set_space_board = false) {
            const t = useWorkspaceTree();
            const item = t.getItemById(item_id);

            if (set_space_board && item) {
                const wid = item.workspaceId;
                const ws = t.getWorkspaceById(wid);

                t.setActiveWorkspaceCategoryId(
                    item.workspaceCategoryId ?? ws?.workspaceCategoryId,
                );
                t.setActiveWorkspaceId(wid);
                t.setActiveBoardCategoryId(item.boardCategoryId);
                t.setActiveBoardId(item.boardId);
                t.setActiveItemCategoryId(item.itemCategoryId);
                t.setActiveItemId(item.id);
            }

            return item;
        },

        getBoardByItemGroupId(item_group_id) {
            const tree = useWorkspaceTree();

            return tree.getBoardById(
                tree.getItemCategoryById(item_group_id)?.boardId,
            );
        },
        getItemGroupByItemId(item_id) {
            const tree = useWorkspaceTree();
            const item = tree.getItemById(item_id);

            return tree.getItemCategoryById(item?.itemCategoryId);
        },
        getItemGroupByItemCategoryId(item_category_id) {
            return (
                useWorkspaceTree().getItemCategoryById(item_category_id) ?? null
            );
        },
        getItemById(item_id) {
            return useWorkspaceTree().getItemById(item_id);
        },

        getBoardByBoardId(board_id, workspace) {
            const tree = useWorkspaceTree();
            const tb = tree.getBoardById(board_id);
            if (
                !tb ||
                (workspace?.id != null && tb.workspaceId != workspace.id)
            ) {
                return null;
            }

            return tb;
        },
        getSpaceByBoardId(board_id) {
            const tree = useWorkspaceTree();
            const board = tree.getBoardById(board_id);

            return tree.getWorkspaceById(board?.workspaceId);
        },
        getBoardGroupByBoardId(board_id, workspace) {
            const tree = useWorkspaceTree();
            const board = tree.getBoardById(board_id);
            const bc = tree.getBoardCategoryById(board?.boardCategoryId);
            if (
                !bc ||
                (workspace?.id != null && bc.workspaceId != workspace.id)
            ) {
                return null;
            }

            return bc;
        },
        getBoardGroupByBoardGroupId(board_group_id, workspace) {
            const tree = useWorkspaceTree();
            const bc = tree.getBoardCategoryById(board_group_id);
            if (
                !bc ||
                (workspace?.id != null && bc.workspaceId != workspace.id)
            ) {
                return null;
            }

            return bc;
        },
        getSpaceByBoardGroupId(board_group_id) {
            const tree = useWorkspaceTree();
            const bc = tree.getBoardCategoryById(board_group_id);

            return tree.getWorkspaceById(bc?.workspaceId);
        },
        getSpaceGroupBySpaceId(space_id) {
            const tree = useWorkspaceTree();
            const ws = tree.getWorkspaceById(space_id);

            return tree.getWorkspaceCategoryById(ws?.workspaceCategoryId);
        },
        getSpaceGroupIndexBySpaceId(space_id) {
            const tree = useWorkspaceTree();
            const ws = tree.getWorkspaceById(space_id);

            return (tree.workspaceCategoryOrderedIds ?? []).indexOf(
                ws?.workspaceCategoryId,
            );
        },
        getSpaceBySpaceId(space_id) {
            return this.getSpaceById(space_id);
        },
        getSpaceGroupBySpaceGroupId(space_group_id) {
            return (
                useWorkspaceTree().getWorkspaceCategoryById(space_group_id) ??
                undefined
            );
        },
        restoreEditorState(document_id, item_id) {
            if (useAppState().is_activity) return;

            if (!document_id && !item_id) return;

            let itemFound = null;

            this.active_board?.item_categories?.find((ic) =>
                ic.items?.find((item) =>
                    item.id == item_id ? (itemFound = item) : false,
                ),
            );

            if (!itemFound)
                return console.log("no item found for opening editor");

            this.setActiveItem(itemFound);
            document_id && this.setEditorView(true);

            if (document_id) {
                this.setEditorView(true);
                this.openEditor(document_id);
            }
        },
        replaceSpace(space) {
            const { category, index } =
                this.getSpaceCategoryAndIndexForSpace(space);

            if (index == -1)
                return console.log(
                    `Workspace ${space?.name} not found in Category`,
                );

            //space_category.workspaces.splice(space_index, 1, space)
            this.active_space = category.workspaces[index];
            this.active_space.has_boards_loaded = true;
            this.active_space.board_categories = space.board_categories;
            this.setActivePanel(BOARDS);
        },
        replaceBoard(board) {
            const { category, index } =
                this.getBoardCategoryAndIndexForBoardInSpace(
                    board,
                    this.active_space,
                );
            if (index == -1)
                return console.log(`Board ${board?.name} Not found to replace`);

            //board_category.boards.splice(board_indedx, 1, board)
            this.active_board = category.boards[index];
            this.active_board.has_items_loaded = true;
            this.active_board.item_categories = board.item_categories;
            this.setActivePanel(ITEMS);
        },
        prepareUrlWithSpaceAndBoardUssingIds(space_id, board_id) {
            space_id = space_id ?? this.active_space.id;
            board_id = board_id ?? this.active_board.id;

            return `/workspaces/${space_id}/boards/${board_id}`;
        },
        prepareUrlWithSpaceAndBoard(space, board) {
            space = space ?? this.active_space;
            board = board ?? this.active_board;

            return `/workspaces/${space.slug}/boards/${board?.slug}`;
        },
        prepareUrlWithSpace(space) {
            space = space ?? this.active_space;
            return `/workspaces/${space.slug}`;
        },
        prepareUrlWithSpaceGroup(space_group) {
            return `/workspace-categories/${space_group.slug}`;
        },
        prepareUrlFoSpaceGroupInvitation(space_group) {
            const url = this.prepareUrlWithSpaceGroup(space_group);
            return `${url}/invite`;
        },
        prepareUrlFoSpaceInvitation(space) {
            const url = this.prepareUrlWithSpace(space);
            return `${url}/invite`;
        },
        prepareUrlForBoardGroupInvitation(board_group) {
            const url = this.prepareUrlWithSpace(this.active_space);
            return `${url}/board-categories/${board_group.id}/invite`;
        },
        prepareUrlForBoardInvitation(board) {
            const url = this.prepareUrlWithSpace(this.active_space);
            return `${url}/boards/${board.id}/invite`;
        },
        prepareUrlForItemGroupInvitation(item_group) {
            const url = this.prepareUrlWithSpaceAndBoard(
                this.active_space,
                this.active_board,
            );
            return `${url}/item-categories/${item_group.id}/invite`;
        },
        prepareUrlForItem(item, item_space = null, item_board = null) {
            item = item ?? this.active_item;

            let item_data = this.getSpaceBoardForItem(item.id);

            if (!item_data?.space || !item_data?.board) {
                console.trace(
                    "Space or Board not found for item",
                    item,
                    item_data?.board,
                    item_data?.space,
                );

                item_data = this.getOrLoadSpaceAndBoardForItemId(item.id);
            }

            const url = this.prepareUrlWithSpaceAndBoard(
                item_data?.space,
                item_data?.board,
            );
            return `${url}/items/${item?.slug}`;
        },
        prepareUrlForItemInvitation(item = null, space = null, board = null) {
            const url = this.prepareUrlForItem(item, space, board);
            return `${url}/invite`;
        },
        prepareUrlForItemConversationInvitation(
            item = null,
            space = null,
            board = null,
        ) {
            const url = this.prepareUrlForItemInvitation(item, space, board);
            return `${url}?conversation=true`;
        },
        prepareUrlForItemInvitationByItemId(item_id, space_id, board_id) {
            return `/workspaces/${space_id}/boards/${board_id}/items/${item_id}/invited-members`;
        },
        prepareUrlForItemAssigned(item = null, space = null, board = null) {
            const url = this.prepareUrlForItem(item, space, board);
            return `${url}/assign`;
        },
        prepareUrlForItemDocument(item = null, space = null, board = null) {
            const url = this.prepareUrlForItem(item, space, board);
            return `${url}/document`;
        },

        prepareUrlForItemId(item_id) {
            const item_data = this.getSpaceBoardForItem(item_id);
            return this.prepareUrlForItem(
                item_data.item?.slug,
                item_data.space.slug,
                item_data.board?.slug,
            );
        },
        getSpaceCategoryAndIndexForSpace(space) {
            const catId = space?.workspaceCategoryId;
            const category = this.space_categories.find((c) => c?.id == catId);
            const index =
                category?.workspaces?.findIndex((ws) => ws.id == space?.id) ??
                -1;

            return {
                category: category,
                index: index < 0 ? -1 : index,
            };
        },

        getBoardCategoryAndIndexForBoardInSpace(board, space) {
            const legacyBcid =
                board?.boardCategoryId ?? board?.board_category_id;
            const tree = useWorkspaceTree();

            if (space?.board_categories?.length) {
                const category = space.board_categories.find(
                    (c) => c.id == legacyBcid,
                );
                const index =
                    category?.boards?.findIndex((b) => b.id == board.id) ?? -1;

                return {
                    category: category,
                    index: index < 0 ? -1 : index,
                };
            }

            const rawBc = tree.getBoardCategoryById(board?.boardCategoryId);
            const orderedIds = rawBc?.boardOrderedIds ?? [];
            const index = orderedIds.indexOf(board?.id);

            return {
                category: rawBc
                    ? {
                          ...rawBc,
                          boards: orderedIds
                              .map((bid) => tree.getBoardById(bid))
                              .filter(Boolean),
                      }
                    : null,
                index: index < 0 ? -1 : index,
            };
        },

        getIndexesForBoardCategory(board_category_to_find) {
            const empty = {
                workspace_index: -1,
                workspace_category_index: -1,
                board_category_index: -1,
            };
            const tree = useWorkspaceTree();
            const bc = tree.getBoardCategoryById(board_category_to_find?.id);
            const workspace = tree.getWorkspaceById(bc?.workspaceId);
            const wsCat = tree.getWorkspaceCategoryById(
                workspace?.workspaceCategoryId,
            );

            if (!bc || !workspace) {
                return empty;
            }

            return {
                workspace_index: (wsCat?.workspaceOrderedIds ?? []).indexOf(
                    workspace.id,
                ),
                workspace_category_index: wsCat
                    ? (tree.workspaceCategoryOrderedIds ?? []).indexOf(wsCat.id)
                    : -1,
                board_category_index: (
                    workspace.boardCategoryOrderedIds ?? []
                ).indexOf(bc.id),
            };
        },

        getSourceAndDestinationItemCategoriesForItem(newItem) {
            const tree = useWorkspaceTree();
            const itemRow = tree.getItemById(newItem?.id);

            return {
                source: tree.getItemCategoryById(itemRow?.itemCategoryId),
                destination: tree.getItemCategoryById(newItem?.itemCategoryId),
            };
        },

        getSourceAndDestinationBoardByItemGroup(newItemGroup) {
            const tree = useWorkspaceTree();
            const sourceBoardId = tree.getItemCategoryById(
                newItemGroup?.id,
            )?.boardId;
            const destBoardId = newItemGroup?.boardId;

            return {
                source: this.getBoardByBoardId(sourceBoardId),
                destination: this.getBoardByBoardId(destBoardId),
            };
        },
        getSourceAndDestinationBoardCategoriesForBoard(newBoard) {
            const tree = useWorkspaceTree();
            const board = this.getBoardByBoardId(newBoard?.id);

            return {
                source: tree.getBoardCategoryById(board?.boardCategoryId),
                destination: tree.getBoardCategoryById(
                    newBoard?.boardCategoryId,
                ),
            };
        },
        getSourceAndDestinationSpaceCategoriesForSpace(newSpace) {
            const tree = useWorkspaceTree();
            const space = this.getSpaceBySpaceId(newSpace?.id);

            return {
                source: tree.getWorkspaceCategoryById(
                    space?.workspaceCategoryId,
                ),
                destination: tree.getWorkspaceCategoryById(
                    newSpace?.workspaceCategoryId,
                ),
            };
        },
        async loadSpaceAndBoardsForItemIds(item_ids) {
            do {
                item_ids =
                    this.filterOutNotFoundItemIdsFromLoadedWorkspaces(item_ids);
                const is_found = await this.loadSpaceBoardAndItemsForItem(
                    item_ids[0],
                );
                //console.log("is_found : ", is_found, item_ids[0], this.findItemInWorkspaces({id: item_ids[0]}));
                item_ids.shift();
            } while (item_ids.length);
        },

        filterOutNotFoundItemIdsFromLoadedWorkspaces(item_ids) {
            const tree = useWorkspaceTree();

            return item_ids.filter((id) => !tree.getItemById(id));
        },
        async getOrLoadSpaceAndBoardForItemId(item_id) {
            let itemInfo = this.getSpaceBoardForItem(item_id);

            if (itemInfo?.item) return itemInfo;

            await this.loadSpaceBoardAndItemsForItem(item_id);
            return this.getSpaceBoardForItem(item_id);
        },
        async loadSpaceBoardAndItemsForItem(item_id) {
            if (!item_id) return false; //console.log("can't load space board and items for item with null id ")

            this.active_item_not_found = true;

            let data = {};
            try {
                const response = await axios.get(
                    `/load-space-board?item=${item_id}`,
                );

                data = response.data;
            } catch (e) {
                throw e;
            }

            if (!data.workspace || !data.board) return false;

            const workspace_group = data.workspace_categories;

            const workspace_data = this.getSpaceCategoryAndIndexForSpace(
                data.workspace,
            );

            if (workspace_group && workspace_data.index == -1) {
                workspace_group.workspaces = [];
                workspace_group.workspaces.push(data.workspace);
            }

            const space_category = this.space_categories.find(
                (category) => category.id == data.workspace_category_id,
            );
            if (!space_category) {
                this.space_categories.push(workspace_group);
            }

            let workspace = data.workspace;
            const { category, index } =
                this.getSpaceCategoryAndIndexForSpace(workspace);

            if (index === -1) {
                category?.workspaces?.push(workspace);
            } else {
                const board_categories = workspace.board_categories;
                workspace = category.workspaces[index];
                if (!workspace.board_categories) {
                    workspace.board_categories = board_categories;
                } else {
                    board_categories?.forEach((board_category) => {
                        const index = workspace.board_categories?.findIndex(
                            (category) => category.id == board_category.id,
                        );
                        if (index == -1) {
                            workspace.board_categories.push(board_category);
                        }
                    });
                    workspace.board_categories?.sort(
                        (a, b) => a.order - b.order,
                    );
                }
            }

            const board = data.board;
            const { category: board_category, index: board_index } =
                this.getBoardCategoryAndIndexForBoardInSpace(board, workspace);

            if (board_index == -1) {
                board_category?.boards?.push(board);
            } else {
                const current_board = board_category?.boards[board_index];
                current_board.item_categories = board?.item_categories;
                //console.log('current-board: ', current_board.name)
            }

            return true;
        },

        async inviteUsersToSpace(invited_profile_ids) {
            this.is_invitation_in_progress = true;
            const space = this.invited_data;
            const data = {
                invited_profile_ids: invited_profile_ids,
            };
            const url = useWorkspace().prepareUrlWithSpace(space);
            return axios
                .post(`${url}/invite`, data)
                .then(({ data }) => {
                    useAppState().toggleInviteDrawer(false);

                    Utilities().toggleSnackBar({
                        message: `${invited_profile_ids.length} new memebers have been invited on ${space.name}`,
                        timeout: 3000,
                        color: "success",
                        location: "bottom right",
                    });

                    this.invited_data = null;
                    this.invite_type = null;
                    this.is_invitation_in_progress = false;
                    return data;
                })
                .catch((error) => {
                    this.is_invitation_in_progress = false;
                });
        },

        async inviteUsersToBoard(invited_profile_ids) {
            this.is_invitation_in_progress = true;
            const board = this.active_chat_data;
            const data = {
                invited_profile_ids: invited_profile_ids,
            };
            const url = useWorkspace().prepareUrlWithSpace(this.active_space);
            return axios
                .post(this.prepareUrlForBoardInvitation(board), data)
                .then(({ data }) => {
                    useAppState().toggleInviteDrawer(false);

                    Utilities().toggleSnackBar({
                        message: `${invited_profile_ids.length} new memebers have been invited on ${board.name}`,
                        timeout: 3000,
                        color: "success",
                        location: "bottom right",
                    });

                    const convData = {
                        name: board.name,
                        type: "board",
                        type_id: board.id,
                        participants: invited_profile_ids,
                    };
                    useConversation().createFakeConversastion(convData);
                    this.invite_type = null;
                    this.invited_data = null;
                    this.is_invitation_in_progress = false;
                    return data;
                })
                .catch((error) => {
                    this.is_invitation_in_progress = false;
                });
        },

        async inviteUsersToSpaceGroup(invited_profile_ids) {
            this.is_invitation_in_progress = true;
            const space_group = this.invited_data;
            const data = {
                invited_profile_ids: invited_profile_ids,
            };
            return axios
                .post(this.prepareUrlFoSpaceGroupInvitation(space_group), data)
                .then(({ data }) => {
                    useAppState().toggleInviteDrawer(false);

                    Utilities().toggleSnackBar({
                        message: `${invited_profile_ids.length} new memebers have been invited on ${space_group.name}`,
                        timeout: 3000,
                        color: "success",
                        location: "bottom right",
                    });

                    this.invite_type = null;
                    this.invited_data = null;
                    this.is_invitation_in_progress = false;
                    return data;
                })
                .catch((error) => {
                    this.is_invitation_in_progress = false;
                });
        },

        async inviteUsersToBoardGroup(invited_profile_ids) {
            this.is_invitation_in_progress = true;
            const board_group = this.active_chat_data;
            const data = {
                invited_profile_ids: invited_profile_ids,
            };
            return axios
                .post(this.prepareUrlForBoardGroupInvitation(board_group), data)
                .then(({ data }) => {
                    useAppState().toggleInviteDrawer(false);

                    Utilities().toggleSnackBar({
                        message: `${invited_profile_ids.length} new memebers have been invited on ${board_group.name}`,
                        timeout: 3000,
                        color: "success",
                        location: "bottom right",
                    });

                    const convData = {
                        name: board_group.name,
                        type: "board-group",
                        type_id: board_group.id,
                        participants: invited_profile_ids,
                    };
                    useConversation().createFakeConversastion(convData);
                    this.invite_type = null;
                    this.is_invitation_in_progress = false;
                    return data;
                })
                .catch((error) => {
                    this.is_invitation_in_progress = false;
                });
        },
        async inviteUsersToItemGroup(invited_profile_ids) {
            this.is_invitation_in_progress = true;
            const item_group = this.active_chat_data;

            const data = {
                invited_profile_ids: invited_profile_ids,
            };

            const url = this.prepareUrlWithSpaceAndBoard(
                this.active_space,
                this.active_board,
            );

            return axios
                .post(`${url}/item-categories/${item_group.id}/invite`, data)
                .then(({ data }) => {
                    useAppState().toggleInviteDrawer(false);

                    Utilities().toggleSnackBar({
                        message: `${invited_profile_ids.length} new memebers have been invited on ${item_group.name}`,
                        // message: `You have successfully invited ${item_group.name} on item group!`,
                        timeout: 3000,
                        color: "success",
                        location: "bottom right",
                    });

                    const convData = {
                        name: item_group.name,
                        type: "item-group",
                        type_id: item_group.id,
                        participants: invited_profile_ids,
                    };

                    useConversation().createFakeConversastion(convData);
                    this.invite_type = null;
                    this.is_invitation_in_progress = false;
                    return data;
                })
                .catch((error) => {
                    this.is_invitation_in_progress = false;
                });
        },
        async inviteUsersToItem(profile_ids) {
            this.is_invitation_in_progress = true;
            const item = this.active_chat_data;
            const data = {
                profile_ids: profile_ids,
            };

            const conversation = useConversation().getForItemId(item.id);

            const new_profile_ids =
                conversation?.filterNewlyInvitedProfilesFromList(profile_ids);
            const deleted_profile_ids =
                conversation?.filterRemovedProfilesFromList(profile_ids);

            const item_board_space = this.getSpaceBoardForItem(item.id);

            const url = this.prepareUrlWithSpaceAndBoard(
                item_board_space.space,
                item_board_space.board,
            );
            return axios
                .post(`${url}/items/${item.slug}/invite`, data)
                .then(({ data }) => {
                    useAppState().toggleInviteDrawer(false, item);
                    Utilities().toggleSnackBar({
                        message: `${new_profile_ids.length} new members have been invited on ${item.name}`,
                        // message: `You have successfully invited ${item_group.name} on item group!`,
                        timeout: 3000,
                        color: "success",
                        location: "bottom right",
                    });

                    //conversation?.sendInvitationForProfiles(new_profile_ids);
                    // conversation?.sendRemovedInvitationForProfiles(
                    //     deleted_profile_ids,
                    // );
                    this.is_invitation_in_progress = false;
                    return data;
                })
                .catch((error) => {
                    this.is_invitation_in_progress = false;
                });
        },
        async assignUsersToItem(
            assigned_profile_id_new,
            assigned_profile_id_old,
        ) {
            if (!assigned_profile_id_new) return;

            const assigned_profile_new = useUserStore().profiles.find(
                (profile) => profile.id == assigned_profile_id_new,
            );
            const item = this.active_chat_data;
            let actions_status = "assign";
            const data = {
                user_profile_id: assigned_profile_id_new,
                action: actions_status,
            };
            const conversation = useConversation().getForItemId(item.id);
            const item_board_space = this.getSpaceBoardForItem(item.id);

            return axios
                .post(
                    this.prepareUrlForItemAssigned(
                        item,
                        item_board_space.space,
                        item_board_space.board,
                    ),
                    data,
                )
                .then(({ data }) => {
                    useTeamPanel().addItemToTaskGroup(
                        assigned_profile_id_new,
                        item,
                        data,
                    );
                    useTeam().setOriginalAssigned(assigned_profile_id_new);

                    // conversation?.sendAssignedMessage(
                    //     assigned_profile_id_new,
                    //     assigned_profile_id_old,
                    // );

                    Utilities().toggleSnackBar({
                        message: `${assigned_profile_new?.email} user profile have been ${actions_status} on ${item.name}`,
                        // message: `You have successfully invited ${item_group.name} on item group!`,
                        timeout: 3000,
                        color: "success",
                        location: "bottom right",
                    });
                    return data;
                });
        },
        addSpaceInCategory(space) {
            const index = this.space_categories.findIndex(
                (ws) => ws.id == space.workspace_category_id,
            );
            this.space_categories[index].workspaces.push(space);
        },

        async moveItems(
            selected_category,
            child_sequence,
            selected_space = this.active_space,
            selected_board = this.active_board,
            movedItemIds = [],
            before_sequence = null,
        ) {
            const url = this.prepareUrlWithSpaceAndBoard(
                selected_space,
                selected_board,
            );

            const movedIdSet = new Set(movedItemIds ?? []);
            const itemsToUpdate =
                selected_category.items?.filter((item) =>
                    movedIdSet.has(item?.id),
                ) ?? [];

            itemsToUpdate.forEach((item) => {
                if (selected_category.automatic_close) {
                    item.status = "closed";
                } else if (item.status === "closed") {
                    item.status = "open";
                    if (
                        item.version &&
                        item.version.current_assignment_status ===
                            "end_assigned_by_completion"
                    ) {
                        item.version.current_assignment_status = null;
                    }
                }

                if (
                    selected_category.automatic_end_assignment &&
                    item.version
                ) {
                    item.version.current_assignment_status =
                        "end_assigned_by_completion";
                }

                if (
                    item.version &&
                    (selected_category.automatic_end_assignment ||
                        selected_category.automatic_close)
                ) {
                    item.version.last_assignee_id =
                        item.version.user_profile_id_assignee;
                }
            });

            const conversations = useConversation().getConversationsByItemIds(
                movedItemIds ?? [],
            );

            conversations.forEach((conv) => {
                if (conv.type === useInvitation().ACCESS_LEVELS.ITEM) {
                    if (!conv?.originalData?.item) return;

                    if (selected_category.automatic_close) {
                        conv.originalData.item.status = "closed";
                    } else if (conv.originalData.item.status === "closed") {
                        conv.originalData.item.status = "open";
                        if (
                            conv.originalData.item.version &&
                            conv.originalData.item.version
                                .current_assignment_status ===
                                "end_assigned_by_completion"
                        ) {
                            conv.originalData.item.version.current_assignment_status =
                                null;
                        }
                    }

                    if (
                        selected_category.automatic_end_assignment &&
                        conv.originalData.item.version
                    ) {
                        conv.originalData.item.version.current_assignment_status =
                            "end_assigned_by_completion";
                    }

                    if (
                        conv.originalData.item.version &&
                        (selected_category.automatic_end_assignment ||
                            selected_category.automatic_close)
                    ) {
                        conv.originalData.item.version.last_assignee_id =
                            conv.originalData.item.version.user_profile_id_assignee;
                    }

                    if (useConversation().active?.id === conv.id) {
                        useConversation().active.originalData.item =
                            conv.originalData.item;
                    }
                }
            });

            const payload = {
                item_group_id: selected_category.id,
                child_sequence,
            };
            if (before_sequence != null && Array.isArray(before_sequence)) {
                payload.before_sequence = before_sequence;
            }
            return axios.patch(`${url}/items/reorder`, payload);
        },

        moveTasks(items, selected_category, order = 0, before_id) {
            let ids = [];
            items.forEach((item) => item?.id && ids.push(item.id));

            return axios.post(`/user-profile-tasks/move`, {
                item_ids: ids,
                task_group_id: selected_category.id,
                order: order,
                before_id: before_id,
            });
        },

        isCategoryAutoEndAssignAndItemHasAssignee(category, item) {
            return (
                category.automatic_end_assignment &&
                item?.version?.user_profile_id_assignee
            );
        },

        isResourceDeletable(type) {
            return !["feedback"].includes(type);
        },
        isResourceEditable(type) {
            return !["feedback"].includes(type);
        },
        isItemTrackable(item) {
            return (
                item?.version?.user_profile_id_assignee ===
                useUserStore().profile?.id
            );
        },
        updateGroupForItems(category, items = []) {
            let cat = this.active_board.item_categories.find(
                (cat) => cat.id === category.id,
            );

            items.forEach((item) => (item.item_category_id = category.id));

            if (cat?.items) cat.items = items;
        },
        updateGroup(group, index) {
            this.active_board.item_categories.splice(index, 1, group);
        },
        removeDuplicateItemGroup() {
            const unique_item_group_ids = new Set();
            this.active_board.item_categories =
                this.active_board.item_categories.filter((item_group) => {
                    if (!item_group?.id) return false;

                    if (!unique_item_group_ids.has(item_group?.id)) {
                        unique_item_group_ids.add(item_group.id);
                        return true;
                    }

                    return false;
                });
        },
        generateItemLink(item) {
            return this.generateLink({
                object_id: item.id,
                object_type: "item",
            });
        },
        async generateLink(payload) {
            return await axios.post(`/links`, payload);
        },

        setEditable(val) {
            this.is_editing_item = val;
        },

        setDisabledHoverMenu() {
            this.is_hover_menu_disabled = true;
        },

        setEnabledHoverMenu() {
            this.is_hover_menu_disabled = false;
        },

        async setActiveItemById(item_id, is_set_selected_item = true) {
            if (!item_id) return;

            const res = await this.getOrLoadSpaceAndBoardForItemId(item_id);
            const t = useWorkspaceTree();
            const item = t.getItemById(item_id);

            if (item?.id) {
                const wid = item.workspaceId ?? item.workspace_id;
                const ws = wid ? t.getWorkspaceById(wid) : null;
                const wcat =
                    item.workspaceCategoryId ??
                    item.workspace_category_id ??
                    ws?.workspaceCategoryId ??
                    ws?.workspace_category_id;
                if (wcat != null) {
                    t.setActiveWorkspaceCategoryId(wcat, false);
                }
                if (wid) {
                    t.setActiveWorkspaceId(wid, false);
                }
                const bcat = item.boardCategoryId ?? item.board_category_id;
                if (bcat != null) {
                    t.setActiveBoardCategoryId(bcat, false);
                }
                const bid = item.boardId ?? item.board_id;
                if (bid) {
                    t.setActiveBoardId(bid, false);
                }
                const icat = item.itemCategoryId ?? item.item_category_id;
                if (icat != null) {
                    t.setActiveItemCategoryId(icat, false);
                }
                t.setActiveItemId(item.id, false);
            }

            const app_state_store = useAppState();
            const widForState = item?.workspaceId ?? item?.workspace_id;
            const spaceForState =
                res.space ??
                (widForState ? t.getWorkspaceById(widForState) : null);
            const boardForState =
                res.board ??
                (item?.boardId ?? item?.board_id
                    ? t.getBoardById(item.boardId ?? item.board_id)
                    : null);

            if (spaceForState?.id) {
                app_state_store.updateWorkspaceId(spaceForState.id);
            }
            if (boardForState?.id) {
                app_state_store.updateBoardId(boardForState.id);
            }

            const toActivate = item ?? res.item;
            if (toActivate?.id) {
                this.setActiveItem(toActivate, is_set_selected_item);
            }

            this.active_item_not_found = !toActivate?.id;
            return res;
        },

        setLastUpdatedItem(val) {
            this.last_updated_item = val;
        },

        setChinItemProfileIds(selected_profile_ids, all_profile_ids) {
            this.chin.item_profile_ids = all_profile_ids;
            this.chin_selected.item_profile_ids = selected_profile_ids;
        },
        setChinBoardProfileIds(selected_profile_ids, all_profile_ids) {
            this.chin.board_profile_ids = all_profile_ids;
            this.chin_selected.board_profile_ids = selected_profile_ids;
        },
        setChinSpaceProfileIds(selected_profile_ids, all_profile_ids) {
            this.chin.space_profile_ids = all_profile_ids;
            this.chin_selected.space_profile_ids = selected_profile_ids;
        },

        setChinItemGroupProfileIds(selected_profile_ids, all_profile_ids) {
            this.chin.item_group_profile_ids = all_profile_ids;
            this.chin_selected.item_group_profile_ids = selected_profile_ids;
        },
        setChinBoardGroupProfileIds(selected_profile_ids, all_profile_ids) {
            this.chin.board_group_profile_ids = all_profile_ids;
            this.chin_selected.board_group_profile_ids = selected_profile_ids;
        },
        setChinSpaceGroupProfileIds(selected_profile_ids, all_profile_ids) {
            this.chin.space_group_profile_ids = all_profile_ids;
            this.chin_selected.space_group_profile_ids = selected_profile_ids;
        },
        getChinProfileIdsByEntityType(entity_type) {
            const entityData = {
                [INVITE.ITEM]: this.setChinItemProfileIds,
                [INVITE.BOARD]: this.setChinBoardProfileIds,
                [INVITE.SPACE]: this.setChinSpaceProfileIds,
                [INVITE.ITEM_GROUP]: this.setChinItemGroupProfileIds,
                [INVITE.BOARD_GROUP]: this.setChinBoardGroupProfileIds,
                [INVITE.SPACE_GROUP]: this.setChinSpaceGroupProfileIds,
            };
            return entityData[entity_type];
        },
        setIsFetchingItem(val) {
            useWorkspaceTree().setIsFetchingItem(val);
        },

        setWorkspacesForWorkSpaceGroup(workspace_group_id, workspaces) {
            const workspace_group =
                this.getSpaceGroupBySpaceGroupId(workspace_group_id);

            if (workspace_group) {
                workspace_group.workspaces = workspaces;
                // this.updateNodeKey(workspace_group);
            }
        },

        setBoardsForBoardGroup(board_group_id, boards) {
            const board_group =
                this.getBoardGroupByBoardGroupId(board_group_id);

            if (board_group) {
                this.updateBoardsCategoryId(board_group_id, boards);
                board_group.boards = boards;
                this.updateNodeKey(board_group);
            }
        },

        updateBoardsCategoryId(board_group_id, boards) {
            boards.forEach((board) => {
                if (board.board_category_id !== board_group_id)
                    board.board_category_id = board_group_id;
            });
        },

        updateNodeKey(object) {
            if (!object) return;

            object.liveSyncKey = Date.now() + (object.id ?? 10);
        },

        removeItemCategoryById(id) {
            if (this.active_board) {
                this.active_board.item_categories =
                    this.active_board.item_categories?.filter((category) => {
                        return category.id !== id;
                    }) ?? [];
            }
        },

        tempUpdateCategory(categories, categoryId, data) {
            const category = categories?.find(
                (category) => category.id === categoryId,
            );

            Object.keys(data).forEach((key) => {
                category[key] = data[key];
            });
        },

        removeFakeItemCatgeory() {
            this.active_board.item_categories =
                this.active_board?.item_categories?.filter(
                    (item_category) => item_category.id !== null,
                );
        },

        removeFakeSpaceCatgeory() {
            this.space_categories = this.space_categories?.filter(
                (space_category) => space_category.id !== null,
            );
        },

        removeFakeBoardCatgeory() {
            this.active_space.board_categories =
                this.active_space.board_categories?.filter(
                    (board_category) => board_category.id !== null,
                );
        },

        // async automaticEndAssignment(category, value) {
        //     this.tempUpdateCategory(
        //         this.active_board?.item_categories,
        //         category.id,
        //         {
        //             automatic_end_assignment: value,
        //         },
        //     );

        //     try {
        //         const { data } = await axios.patch(
        //             this.item_categories_url + `/${category.slug}`,
        //             category,
        //         );
        //         this.tempUpdateCategory(
        //             this.active_board?.item_categories,
        //             category.id,
        //             { slug: data.slug },
        //         );
        //     } catch {}
        // },
        async automaticClose(category, value) {
            const payload = { automatic_close: value };
            if (value) {
                payload.automatic_end_assignment = value;
                category.automatic_end_assignment = value;
            }

            this.tempUpdateCategory(
                this.active_board?.item_categories,
                category.id,
                payload,
            );

            try {
                const { data } = await axios.patch(
                    this.item_categories_url + `/${category.slug}`,
                    category,
                );
                this.tempUpdateCategory(
                    this.active_board?.item_categories,
                    category.id,
                    { slug: data.slug },
                );
            } catch {}
        },
        async deleteWorkspaceCategoty(category) {
            try {
                await axios.delete(
                    this.item_categories_url + `/${category.slug}`,
                );
                this.removeItemCategoryById(category.id);
            } catch (err) {
                console.log("err", err);
            }
        },
        async createItemCategory(category) {
            try {
                this.tempUpdateCategory(
                    this.active_board?.item_categories,
                    null,
                    { board_id: this.active_board.id },
                );
                const { data } = await axios.post(
                    this.item_categories_url,
                    category,
                );
                this.tempUpdateCategory(
                    this.active_board?.item_categories,
                    null,
                    {
                        allow_to_create_item: true,
                        id: data.id,
                        name: data.name,
                        slug: data.slug,
                        items: [],
                    },
                );
                this.new_item_category_id = data.id;
                this.removeDuplicateItemGroup();
                this.activeItemCategory =
                    this.active_board?.item_categories?.[0];
            } catch (error) {
                if (error?.response?.status === 403) {
                    utilities().showErrorSnackbar("Permission Denied");
                    this.removeFakeItemCatgeory();
                }
            }
        },
        async updateItemCategory(category, url = this.item_categories_url) {
            try {
                axios.patch(url + `/${category.slug}`, { name: category.name });
                useConversation().updateNameByType(category);
            } catch {}
        },
        async createBoardCategory(category) {
            try {
                this.tempUpdateCategory(
                    this.active_space?.board_categories,
                    null,
                    { space_id: this.active_space.id },
                );
                const { data } = await axios.post(
                    this.board_categories_url,
                    category,
                );
                this.tempUpdateCategory(
                    this.active_space?.board_categories,
                    null,
                    {
                        allow_to_create_board: true,
                        id: data.id,
                        name: data.name,
                        slug: data.slug,
                        boards: [...(data?.boards.length ? data.boards : [])],
                    },
                );
                return true;
            } catch (error) {
                if (error?.response?.status === 403) {
                    utilities().showErrorSnackbar("Permission Denied");
                    this.removeFakeBoardCatgeory();
                }
                return false;
            }
        },
        async updateBoardCategory(
            category,
            baseUrl = this.board_categories_url,
        ) {
            try {
                axios.patch(baseUrl + `/${category.slug}`, {
                    name: category.name,
                });
                useConversation().updateNameByType(category);
            } catch {}
        },
        async createWorkspaceCategory(category) {
            try {
                this.tempUpdateCategory(this?.space_categories, null, {});
                const { data } = await axios.post(
                    this.space_categories_url,
                    category,
                );
                this.tempUpdateCategory(this?.space_categories, null, {
                    id: data.id,
                    name: data.name,
                    slug: data.slug,
                    workspaces: [
                        ...(data?.workspaces.length ? data.workspaces : []),
                    ],
                });
                return true;
            } catch (error) {
                if (error?.response?.status === 403) {
                    utilities().showErrorSnackbar("Permission Denied");
                    this.removeFakeSpaceCatgeory();
                }
                return false;
            }
        },
        async updateSpaceCategory(category) {
            try {
                axios.patch(this.space_categories_url + `/${category.slug}`, {
                    name: category.name,
                });
                useConversation().updateNameByType(category);
            } catch {}
        },
        removeFakeItem(itemCatgeoryId) {
            const itemCategory = this.active_board?.item_categories?.find(
                (itemCategory) => {
                    return itemCategory.id === itemCatgeoryId;
                },
            );
            if (!itemCategory) return;
            itemCategory.items = itemCategory.items?.filter(
                (item) => item.id !== null,
            );
        },

        async exportItemCsv(itemId) {
            try {
                return await axios.post(
                    `/reports/workspaces/download`,
                    {
                        user_profile_ids: [],
                        by: "chronology",
                        timezone: DateUtilities.getTimezone(),
                        items: [itemId],
                        date: DateUtilities.getStartAndEndDateFromToday(0),
                    },
                    { responseType: "arraybuffer" },
                );
            } catch {}
        },

        tempUpdateItem(categoryId, itemId, data) {
            const itemCategory = this.active_board.item_categories?.find(
                (item_category) => {
                    return item_category.id === categoryId;
                },
            );

            if (!itemCategory) return;
            const item = itemCategory.items?.find((item) => item.id === itemId);
            if (!item) return;

            Object.keys(data).forEach((key) => {
                item[key] = data[key];
            });
        },

        tempUpdateBoard(categoryId, boardId, data) {
            const boardCategory = this.active_space.board_categories?.find(
                (board_category) => {
                    return board_category.id === categoryId;
                },
            );
            if (!boardCategory) return;
            const board = boardCategory.boards?.find(
                (board) => board.id === boardId,
            );
            if (!board) return;

            Object.keys(data).forEach((key) => {
                board[key] = data[key];
            });
        },

        async createItem(item) {
            const itemCategory = this.active_board.item_categories?.find(
                (item_category) => item_category.id === item.item_category_id,
            );

            if (!itemCategory) return {};

            const tempId = "temp-" + Date.now();
            const tempItem = { ...item, id: tempId, isPending: true };
            itemCategory.items.push(tempItem);
            const index = itemCategory.items.length - 1;

            try {
                const { data } = await axios.post(this.items_url, {
                    ...item,
                    type: ITEM_TYPE.TASK,
                });

                const currentItem = itemCategory.items[index];

                Object.assign(currentItem, {
                    ...data,
                    isPending: false,
                    is_being_created: false,
                    auto_focus: false,
                });

                return currentItem;
            } catch (error) {
                itemCategory.items.splice(index, 1);

                if (error?.response?.status === 403) {
                    utilities().showErrorSnackbar("Permission Denied");
                }

                return {};
            }
        },

        async updateItem(item) {
            if (!item.id) {
                return;
            }

            try {
                const { data } = await axios.patch(
                    this.items_url + `/${item.slug}`,
                    item,
                );
                this.tempUpdateItem(item.item_category_id, item.id, {
                    slug: data.slug,
                    name: data.name,
                });
            } catch (error) {}
            useAppState().updateSelectionHistoryByItem(item);
            useConversation().updateNameByItem(item);
            useTeamPanel().updateItemNameByItemId(item);
            useTimeTrackerStore().updatePausedItemName(item);
            useActivity().updateSelectedActivityName(item);
        },

        tempUpdateSpace(categoryId, spaceId, data) {
            const spaceCategory = this.space_categories?.find(
                (space_category) => {
                    return space_category.id === categoryId;
                },
            );
            if (!spaceCategory) return;
            const space = spaceCategory.workspaces?.find(
                (space) => space.id === spaceId,
            );
            if (!space) return;

            Object.keys(data).forEach((key) => {
                space[key] = data[key];
            });
        },

        addFakeSpace(type) {
            const category = this.space_categories?.find((category) => {
                return category?.id === this.fakeSpaceDetails.categoryId;
            });

            category?.workspaces?.unshift({
                id: null,
                name: "",
                workspace_category_id: this.fakeSpaceDetails.categoryId,
                type,
                entity_type: "workspace",
            });
        },
        async createBoard(board) {
            const boardCategory = this.active_space.board_categories?.find(
                (board_category) => {
                    return board_category.id === board.board_category_id;
                },
            );
            if (!boardCategory) return false;

            const tempId = "temp-" + Date.now();
            const tempBoard = { ...board, id: tempId, isPending: true };
            boardCategory.boards.push(tempBoard);
            const index = boardCategory.boards.length - 1;

            try {
                const { data } = await axios.post(this.boards_url, {
                    ...board,
                    type: INVITE.BOARD,
                });
                const currentBoard = boardCategory.boards[index];
                Object.assign(currentBoard, {
                    ...data,
                    isPending: false,
                    is_being_created: false,
                    auto_focus: false,
                    item_categories: [],
                });
                this.setActiveBoard(currentBoard, false);
                return true;
            } catch (error) {
                boardCategory.boards.splice(index, 1);
                if (error?.response?.status === 403) {
                    utilities().showErrorSnackbar("Permission Denied");
                }
                return false;
            }
        },
        async updateBoard(board) {
            try {
                const { data } = await axios.patch(
                    this.boards_url + `/${board.slug}`,
                    { name: board.name },
                );
                this.tempUpdateItem(board.board_category_id, board.id, {
                    slug: data.slug,
                });
            } catch (error) {}
            useConversation().updateNameByType(board);
            useAppState().updateSelectionHistoryByItem(board);
        },
        removeFakeSpace(space_category_id) {
            const spaceCategory = this.space_categories?.find(
                (spaceCategory) => {
                    return spaceCategory.id === space_category_id;
                },
            );
            if (!spaceCategory) return;
            spaceCategory.workspaces = spaceCategory.workspaces?.filter(
                (space) => space.id !== null,
            );
        },
        async updateSpace(space) {
            try {
                const { data } = await axios.patch(
                    this.spaces_url + `/${space.slug}`,
                    { name: space.name },
                );
                this.tempUpdateSpace(space.workspace_category_id, space.id, {
                    slug: data.slug,
                });
            } catch (error) {
                console.log(error);
            }
            useConversation().updateNameByType(space);
            useAppState().updateSelectionHistoryByItem(space);
        },
        async createSpace(space) {
            try {
                const { data } = await axios.post(this.spaces_url, {
                    ...space,
                    type: INVITE.SPACE,
                });
                this.tempUpdateSpace(space.workspace_category_id, space.id, {
                    id: data.id,
                    slug: data.slug,
                    name: data.name,
                    board_categories: [],
                    auto_focus: false,
                });
                return true;
            } catch (error) {
                if (error?.response?.status === 403) {
                    utilities().showErrorSnackbar("Permission Denied");
                    this.removeFakeSpace(space.workspace_category_id);
                } else {
                    utilities().showErrorSnackbar(error.message);
                }
                return false;
            }
        },
        async deleteBoard(board, index) {
            try {
                await axios.delete(this.boards_url + `/${board.slug}`);

                const boardCategory = this.active_space?.board_categories?.find(
                    (board_category) => {
                        return board_category?.id === board?.board_category_id;
                    },
                );

                boardCategory?.boards?.splice(index, 1);
            } catch (error) {}
        },
        async deleteSpace(space) {
            const conversationStore = useConversation();
            try {
                await axios.delete(this.spaces_url + `/${space.slug}`);

                const data = this.getSpaceCategoryAndIndexForSpace(space);

                data?.category?.workspaces?.splice(data.index, 1);
                if (this.active_space?.id === space?.id) {
                    this.resetActiveWorkspace();
                    if (conversationStore.active?.space_id === space?.id) {
                        const id = conversationStore.active?.id;
                        conversationStore.removeConversationsFromHistory([id]);
                        conversationStore.removeConversationById(id);
                    }
                }
            } catch (error) {
                console.log(error);
            }
        },
        async removeItems(categoryId, itemIds) {
            const itemCategory = this.active_board?.item_categories?.find(
                (item_category) => {
                    return item_category?.id === categoryId;
                },
            );

            itemCategory.items =
                itemCategory?.items?.filter((item) => {
                    return !itemIds?.includes(item.id);
                }) ?? [];
        },

        removeWorkPanelItemBasedOnPermission(
            workpanel_object,
            lineage_resource,
        ) {
            if (workpanel_object.type !== "item") {
                return;
            }

            const t = useWorkspaceTree();
            const item_group = this.getItemGroupByItemId(
                lineage_resource?.item?.id,
            );
            const board = this.getBoardByItemGroupId(
                lineage_resource?.item_group?.id,
            );
            const board_group = this.getBoardGroupByBoardId(
                lineage_resource?.board?.id,
            );
            const workspace = this.getSpaceById(
                lineage_resource?.workspace?.id,
            );
            const workspace_category = this.getSpaceGroupBySpaceGroupId(
                lineage_resource?.workspace_group?.id,
            );

            this.removeItemById(workpanel_object.id);

            const icAfter = t.getItemCategoryById(item_group?.id);
            if (icAfter && (icAfter.itemOrderedIds ?? []).length === 0) {
                this.removeItemGroupById(item_group?.id);
            }

            const boardAfter = t.getBoardById(board?.id);
            if (
                boardAfter &&
                (boardAfter.itemCategoryOrderedIds ?? []).length === 0
            ) {
                this.removeBoardById(board?.id);
            }

            const bgAfter = t.getBoardCategoryById(board_group?.id);
            if (bgAfter && (bgAfter.boardOrderedIds ?? []).length === 0) {
                this.removeBoardGroupById(board_group?.id);
            }

            const wsAfter = t.getWorkspaceById(workspace?.id);
            if (
                wsAfter &&
                (wsAfter.boardCategoryOrderedIds ?? []).length === 0
            ) {
                this.removeSpaceById(workspace?.id);
            }

            const wcatAfter = t.getWorkspaceCategoryById(
                workspace_category?.id,
            );
            if (
                wcatAfter &&
                (wcatAfter.workspaceOrderedIds ?? []).length === 0
            ) {
                this.removeSpaceById(workspace?.id);
            }
        },
        redirectToActiveItem() {
            if (!this.active_item) return;

            const elId = `auto-scroll-${this.active_item?.id}${this.active_item?.slug}`;
            const element = document?.getElementsByClassName(elId)?.[0];
            nextTick(() => {
                if (!this.activeItemSavedScrollPosition && element) {
                    element.scrollIntoView?.({
                        block: "end",
                    });
                } else if (element) {
                    const categoryElement = element.closest(
                        ".propus-items-container",
                    );
                    const categoriesElement = element.closest(
                        ".prospus-draggable-categories",
                    );
                    const { item, category } =
                        this.activeItemSavedScrollPosition;
                    categoriesElement.scrollBy(category.x, category.y);

                    categoryElement.scrollBy(item.x, item.y);
                    this.activeItemSavedScrollPosition = null;
                }

                this.setActiveItemLocator();
            });
        },
        redirectToActiveSpace() {
            if (!this.active_space) return;
            const elId =
                "id-" + this.active_space?.id + this.active_space?.slug;
            if (elId) {
                nextTick(() => {
                    const activeSpaceColumn = this.getSpaceGroupBySpaceId(
                        this.active_space.id,
                    );
                    const scrollContainer = document.querySelector(
                        "#prospus-workpanel-spaces-container",
                    );

                    if (!scrollContainer) return;

                    let activeSpaceOrder = activeSpaceColumn?.order;
                    if (
                        activeSpaceOrder === null ||
                        activeSpaceOrder === undefined
                    ) {
                        const spaceIndex = this.getSpaceGroupIndexBySpaceId(
                            this.active_space.id,
                        );
                        activeSpaceOrder = spaceIndex;
                    }
                    const totalColumns = scrollContainer.children.length;
                    const containerWidth = scrollContainer.offsetWidth;
                    const columnWidth = scrollContainer.children[0].offsetWidth;
                    const availableColumns = totalColumns - activeSpaceOrder;

                    const visibleColumnsCount = Math.trunc(
                        containerWidth / columnWidth,
                    );
                    const isSpaceOrderLessThanVisibleColumns =
                        activeSpaceOrder < visibleColumnsCount;

                    const isSpaceOrderLessThanAvailableColumns =
                        activeSpaceOrder < availableColumns;

                    const isSpaceVisible =
                        containerWidth > availableColumns * columnWidth * 0.5;

                    const canBeShownNearest =
                        isSpaceVisible ||
                        isSpaceOrderLessThanVisibleColumns ||
                        isSpaceOrderLessThanAvailableColumns;

                    document.getElementById(elId)?.scrollIntoView?.({
                        block: "end",
                        inline: canBeShownNearest ? "nearest" : "start",
                    });

                    this.setActiveItemLocator();
                    setTimeout(() => {
                        if (this.active_item_locator_temp) {
                            this.active_item_locator =
                                this.active_item_locator_temp;
                            this.active_item_locator_temp = null;
                        }
                    }, 1200);
                });
            }
        },
        redirectToActiveBoard() {
            if (!this.active_board) return;
            const elId = `auto-scroll-${this.active_board?.id}${this.active_board?.slug}`;
            this.redirectToActive(elId);
        },

        redirectToActiveBoardGroup() {
            if (!this.activeBoardCategory) return;
            this.openBoardsPanel();
            const elId = `auto-scroll-${this.activeBoardCategory?.id}${this.activeBoardCategory?.slug}`;
            this.redirectToActive(elId);
        },

        redirectToActiveSpaceGroup() {
            if (!this.activeSpaceCategory) return;
            this.openSpacesPanel();
            const elId = `auto-scroll-${this.activeSpaceCategory?.id}${this.activeSpaceCategory?.slug}`;
            this.redirectToActive(elId);
        },

        redirectToActive(id = this.active_item_locator_temp) {
            const elem = document.querySelector(`.${id}`);
            elem?.scrollIntoView?.({ block: "end" });

            this.setActiveItemLocator();
        },

        setActiveItemLocator() {
            setTimeout(() => {
                if (this.active_item_locator_temp) {
                    this.active_item_locator = this.active_item_locator_temp;
                    this.active_item_locator_temp = null;
                }
            }, 1200);
        },

        updateItemAssignee(item_id, version) {
            const item = this.getItemById(item_id);
            if (item) {
                item.version = version;
            }

            const legacyItems = this.getItemGroupByItemId(item_id)?.items;
            if (Array.isArray(legacyItems)) {
                const i = legacyItems.findIndex((row) => row.id == item_id);
                if (i !== -1) {
                    legacyItems[i] = item;
                }
            }
        },
        isElementInViewport(element) {
            const rect = element.getBoundingClientRect();
            return (
                rect.top >= 0 &&
                rect.left >= 0 &&
                rect.bottom <=
                    (window.innerHeight ||
                        document.documentElement.clientHeight) &&
                rect.right <=
                    (window.innerWidth || document.documentElement.clientWidth)
            );
        },
        toggleDocument(
            show = false,
            convId = undefined,
            panel = "",
            type_id = undefined,
            type = undefined,
            is_open_from_hover_menu = false,
        ) {
            this.documentDetails = {
                open: show,
                conv_id: show ? convId : undefined,
                documentable_id: type_id,
                documentable_type: type,
                panel,
                is_open_from_hover_menu,
            };
        },
        async updateBoardAvatar(workspaceSlug, boardSlug, avatarBlob) {
            try {
                const avatar = await blobToBase64(avatarBlob);
                const res = await axios.post(
                    `/workspaces/${workspaceSlug}/boards/${boardSlug}/avatar`,
                    { avatar },
                );
                return res;
            } catch (error) {
                console.log(error);
            }
        },
        async initializeNewSpaceCategoryAndNavigateToItems() {
            await new Promise((resolve) => setTimeout(resolve, 500));

            const workspaceCategory = this.space_categories.at(0);

            this.initializeNewSpaceAndNavigateToItems(workspaceCategory);
        },
        async initializeNewSpaceAndNavigateToItems(
            workspaceCategory = this.activeSpaceCategory,
        ) {
            const workspace = workspaceCategory?.workspaces.at(0);

            this.setActiveWorkspace(workspace, false);
            await new Promise((resolve) => setTimeout(resolve, 500));

            this.activeSpaceCategory = null;

            this.openBoardsPanel();

            await new Promise((resolve) => setTimeout(resolve, 500));

            this.initializeNewBoardAndNavigateToItems();
        },

        async initializeNewBoardAndNavigateToItems(
            boardCategory = this.active_space?.board_categories.at(0),
        ) {
            const index = boardCategory?.boards?.length
                ? boardCategory?.boards.length - 1
                : 0;
            const currentBoard = boardCategory?.boards?.at(index);

            this.setActiveBoard(currentBoard, false);
            RouteHelper.updateUrlForEntity(currentBoard);
            this.activeBoardCategory = null;
            this.setActiveItem(null);

            await new Promise((resolve) => setTimeout(resolve, 500));

            this.openItemsPanel();
            this.activeItemCategory = currentBoard?.item_categories?.at(0);
        },
        async closeItem(itemIds) {
            try {
                await axios.post(`/items/close`, {
                    ids: itemIds,
                });
                utilities().showSuccessSnackbar("Item closed successfully");
            } catch (error) {}
        },

        async getItemDataFromItemId(itemId) {
            try {
                const { data } = await axios.get(`/items/${itemId}`);
                return data;
            } catch (error) {
                console.log(error);
                return {};
            }
        },
        async hasItemCloseAccess(itemId) {
            try {
                const { data } = await axios.get(
                    `/work-panel/items/${itemId}/permissions/move`,
                );
                return !!data?.data;
            } catch (error) {
                console.log(error);
                return false;
            }
        },
        async setItemDataInDocument(id) {
            if (!id) return;
            const item = this.findItemInWorkspaces(id);
            if (!item) {
                const itemData = await this.getItemDataFromItemId(id);
                this.setActiveChatData(itemData);
            } else {
                this.setActiveChatData(item);
            }
        },
        resetActiveWorkspace() {
            this.active_space = null;
            this.active_board = null;
            this.active_item = null;
            this.activeBoardCategory = null;
            this.activeItemCategory = null;
        },

        setLocateClassName(type, item) {
            switch (type) {
                case "space":
                    item = item || this.active_space;
                    break;
                case "board":
                    item = item || this.active_board;
                    break;
                case "item":
                    item = item || this.active_item;
                    break;
                default:
                    break;
            }

            this.active_item_locator = null;
            this.active_item_locator_type = type;
            this.active_item_locator_temp = `auto-scroll-${item?.id}${item?.slug}`;
        },

        getItemGroupByGroupIdAndActiveBoard(group_id) {
            return this.active_board?.item_categories?.find(
                (item_category) => item_category.id == group_id,
            );
        },

        getBoardGroupByGroupIdAndActiveSpace(group_id) {
            return this.active_space?.board_categories?.find(
                (board_category) => board_category.id == group_id,
            );
        },

        getSpaceGroupByGroupIdAndActiveWorkspace(group_id) {
            return this.space_categories?.find(
                (space_category) => space_category.id == group_id,
            );
        },

        resetActiveItemLocator() {
            //this.active_item_locator_temp = null;
            this.active_item_locator = null;
            this.active_item_locator_cancel = false;
        },

        setRunningCreateItemIds(id) {
            this.runningCreateItemIds.last = id;
            this.runningCreateItemIds.ids.set(id, true);
        },

        removeRunningCreateItemIds(id) {
            this.runningCreateItemIds.ids.delete(id);
            if (this.runningCreateItemIds.last === id) {
                this.runningCreateItemIds.last = null;
            }
        },
        async loadWorkspaceCategories() {
            try {
                const { data } = await axios.get("/workspace-categories");
                if (data && Array.isArray(data)) {
                    this.space_categories = data;
                }
            } catch (error) {
                console.error("Failed to load workspace categories:", error);
            }
        },
        syncItem(item) {
            if (!item) return;

            const foundedItem = this.getItemById(item.id);
            if (!foundedItem) return;
            Object.keys(item ?? {}).forEach((key) => {
                if (typeof item[key] === typeof foundedItem[key]) {
                    if (
                        Array.isArray(item[key]) ||
                        typeof item[key] !== "object"
                    ) {
                        foundedItem[key] = item[key];
                    } else {
                        foundedItem[key] = {
                            ...foundedItem[key],
                            ...item[key],
                        };
                    }
                }
            });
            const categoryInActiveBoard =
                this.active_board?.item_categories?.find(
                    (category) => category.id === item.item_category_id,
                );

            if (!categoryInActiveBoard) return;
            const foundedItemInCategory = categoryInActiveBoard.items?.find(
                (i) => i.id == item.id,
            );
            if (!foundedItemInCategory) return;
            Object.keys(item ?? {}).forEach((key) => {
                if (typeof item[key] === typeof foundedItemInCategory[key]) {
                    if (
                        Array.isArray(item[key]) ||
                        typeof item[key] !== "object"
                    ) {
                        foundedItemInCategory[key] = item[key];
                    } else {
                        foundedItemInCategory[key] = {
                            ...foundedItemInCategory[key],
                            ...item[key],
                        };
                    }
                }
            });
        },
    },
});
